import { useAccountsStore } from '@/entities/account';
import {
  useAppSettingsStore,
  useExtractUserName,
  useMediaQuery,
} from '@/shared/lib';
import { cn } from '@/shared/utils';
import { useConfirmationModal, useUserProfileDialog } from '@/widgets';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserBalance } from './UserBalance';
import { UserMenu } from './UserMenu';
import { UserBalanceSkeleton } from './UserBalanceSkeleton';
import { useSessionStore } from '@/entities/session';

const MOBILE_CLOSED_WIDTH = 'w-full';
const MOBILE_OPEN_WIDTH = 'w-[calc(100vw-2.4rem)]';

export function UserBalanceDropdown() {
  const isMobile = useMediaQuery('(max-width: 1024px)');
  const isTablet = useMediaQuery('(max-width: 1067px)');
  const { openModal, Modal: ConfirmationModal } = useConfirmationModal();
  const { t } = useTranslation();
  const { logout, session } = useSessionStore();
  const general = useAccountsStore(state => state.general);
  const { baseAsset, isBalancesHidden } = useAppSettingsStore();

  const [open, setOpen] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const isCorporate =
    session?.additional?.isCorporate && session?.additional?.isVerified;

  const { userFullName, isUserProfileInfoLoading } = useExtractUserName();

  const isUserVerified = session?.additional?.isVerified || false;

  const { openUserProfileDialog } = useUserProfileDialog();

  const closeMenu = () => {
    setTooltipOpen(false);
    setOpen(false);
  };

  const handleOpenUserProfileDialog = () => {
    closeMenu();
    openUserProfileDialog();
  };

  const handleLogoutClick = () => {
    closeMenu();
    openModal({
      title: t('confirmationModal.logout.title'),
      description: t('confirmationModal.logout.description'),
      confirmText: t('confirmationModal.logout.confirmText'),
      cancelText: t('confirmationModal.logout.cancelText'),
      onConfirm: () => logout(),
    });
  };

  if (isUserProfileInfoLoading || !general) {
    return (
      <div className={cn('relative w-full', isMobile ? 'left-[-1.2rem]' : '')}>
        <UserBalanceSkeleton />
      </div>
    );
  }

  return (
    <>
      {/* Mobile menu dark overlay to close the menu when clicking outside */}
      {isMobile && open && (
        <div
          className="fixed inset-0 z-70 bg-primary/50 tablet:hidden"
          onClick={closeMenu}
        />
      )}

      <div
        className={cn(
          'relative max-w-[35.2rem] w-full',
          isTablet ? 'left-[-1.2rem]' : ''
        )}
        onMouseEnter={() => !isMobile && setOpen(true)}
        onMouseLeave={() => !isMobile && closeMenu()}>
        <div className={cn(isMobile ? MOBILE_CLOSED_WIDTH : 'w-full')}>
          <UserBalance
            isBalancesHidden={isBalancesHidden}
            userFullName={userFullName}
            balance={general?.balance}
            deltaPercent={general?.deltaPercent}
            baseAsset={baseAsset}
            isUserVerified={isUserVerified}
            isCorporate={isCorporate}
            withChevron
            onClick={() => setOpen(prev => !prev)}
          />
        </div>

        <div
          className={cn(
            'absolute left-0 right-0 top-0 rounded-[2.4rem] bg-background shadow-smooth',
            'transition-all duration-400 overflow-hidden',
            open ? 'opacity-100 z-80' : 'opacity-0 pointer-events-none z-0',
            isMobile
              ? open
                ? MOBILE_OPEN_WIDTH
                : MOBILE_CLOSED_WIDTH
              : 'w-full'
          )}>
          <UserBalance
            isBalancesHidden={isBalancesHidden}
            userFullName={userFullName}
            isUserVerified={isUserVerified}
            balance={general?.balance}
            deltaPercent={general?.deltaPercent}
            baseAsset={baseAsset}
            isCorporate={isCorporate}
            withChevron={false}
            withCloseButton={isMobile}
            onClose={closeMenu}
          />

          <UserMenu
            open={open}
            tooltipOpen={tooltipOpen}
            setTooltipOpen={() => setTooltipOpen(!tooltipOpen)}
            onOpenProfile={handleOpenUserProfileDialog}
            onLogout={handleLogoutClick}
            isUserVerified={isUserVerified}
          />
        </div>

        {ConfirmationModal}
      </div>
    </>
  );
}
